import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";
import { Outlet, ScrollRestoration, useLocation } from "react-router";

import { AuthProvider } from "authentication/AuthProvider";
import { useAuthInit } from "authentication/useAuthInit";
import { useFetch } from "common/utilities/apiFetch";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useSession } from "context/session/useSession";
import { AudioPlayerProvider } from "domains/audioPlayer/components/AudioPlayerProvider";
import { getIdToken, getUserId, getUserRole } from "domains/authentication/selectors";
import { fetchBookmarks } from "domains/bookmarks/actions";
import { CallsToAction } from "domains/callsToAction/CallsToAction";
import { CoachmarkStack } from "domains/coachmarks/components/CoachmarkStack";
import { fetchCollections } from "domains/collection/actions";
import { CollectionType } from "domains/collection/types/collectionType";
import { fetchTerms } from "domains/language/actions";
import { getSiteLanguage } from "domains/language/selectors";
import { ModalProvider } from "domains/modal/components/ModalProvider";
import { GlobalNav } from "domains/nav/GlobalNav";
import { fetchNotifications } from "domains/notifications/actions";
import { useFetchTopicMetadata } from "domains/topic/hooks/useFetchTopicMetadata";
import { VisualMediaPlayerProvider } from "domains/visualMediaPlayer/components/VisualMediaPlayerProvider";
import { fetchWhatsNew } from "domains/whatsNew/actions";
import { fetchLanguages } from "entities/languages/actions";
import { getCanDisplayBookmarks, getShouldDisplayNotifications } from "entities/permissions/selectors";
import { useFetchPermissions } from "entities/permissions/useFetchPermissions";
import { fetchHighlightedTopics } from "entities/topics/actions";
import type { UserInfoPayload } from "entities/user/types";
import { userSlice } from "entities/user/user.slice";
import { ErrorBoundary } from "router/ErrorBoundary";
import { MobileAppBanner } from "router/MobileAppBanner";

export const RootOutlet = () => (
  <ErrorBoundary>
    <AuthProvider>
      <ScrollRestoration />
      <MobileAppBanner />
      <GlobalNav />
      <Outlet />
      <AudioPlayerProvider />
      <CallsToAction />
      <CoachmarkStack />
      <ModalProvider />
      <VisualMediaPlayerProvider />
      <Effects />
    </AuthProvider>
  </ErrorBoundary>
);

const Effects = () => {
  useAuthInit();
  const dispatch = useDispatch();
  const fetch = useFetch();
  const fetchPermissions = useFetchPermissions();
  const fetchTopicMetadata = useFetchTopicMetadata();
  const showBookmarks = useSelector(getCanDisplayBookmarks);
  const idToken = useSelector(getIdToken);
  const shouldDisplayNotifications = useSelector(getShouldDisplayNotifications);
  const siteLanguage = useSelector(getSiteLanguage);
  const userId = useSelector(getUserId);
  const userRole = useSelector(getUserRole);
  const track = useTrack();

  useNavigationEffects();

  useEffect(() => {
    window.dataLayer.push({ user_id: userId, user_type: userRole.toLowerCase() });
  }, [dispatch, userId, userRole]);

  useEffect(() => {
    if (shouldDisplayNotifications) dispatch(fetchNotifications({ offset: 0 }));
  }, [dispatch, shouldDisplayNotifications]);

  useEffect(() => {
    if (showBookmarks) dispatch(fetchBookmarks({ offset: 0 }));
  }, [dispatch, showBookmarks]);

  useEffect(() => {
    fetchTopicMetadata();
    dispatch(fetchHighlightedTopics());
    dispatch(fetchCollections(CollectionType.Featured));
    dispatch(fetchCollections(CollectionType.Listed));
    dispatch(fetchCollections(CollectionType.Editable));
  }, [dispatch, fetchTopicMetadata, idToken, siteLanguage]);

  useEffect(() => {
    if (idToken === undefined) void fetchPermissions();
  }, [fetchPermissions, idToken]);

  useEffect(() => {
    if (idToken === undefined) return;
    void fetch("userinfo", { idToken })
      .then((response) => response.json())
      .then((body: UserInfoPayload) => {
        const user = body["http://sdy.ai/metadata"];
        dispatch(userSlice.actions.setUser(user));
        mixpanel.identify(user.ext_id);
        track({ eventType: "successful login", name: "Login", userId: user.ext_id });
      });
  }, [dispatch, fetch, idToken, track]);

  useEffect(() => void dispatch(fetchWhatsNew()), [dispatch, siteLanguage]);

  useEffect(() => {
    dispatch(fetchLanguages());
    dispatch(fetchTerms({ initialising: true, language: siteLanguage }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return null;
};

const useNavigationEffects = () => {
  const location = useLocation();
  const { scrollPositions, topicBack, updateSession } = useSession();
  const [topicBackConsumed, setTopicBackConsumed] = useState<boolean>(false);

  /**
   * Once a 'topicBack' object has been used to set back button properties on a topic page:
   * When the user navigates away from the topic page or one of its sub-pages,
   * then clear the 'topicBack' state
   */
  useEffect(() => {
    if (topicBack === undefined) return;
    if (!topicBackConsumed && location.pathname.startsWith("/topics/") && location.pathname !== "/topics/")
      setTopicBackConsumed(true);
    if (topicBackConsumed && (!location.pathname.startsWith("/topics/") || location.pathname === "/topics/")) {
      updateSession((state) => ({ ...state, topicBack: undefined }));
      setTopicBackConsumed(false);
    }
  }, [location.pathname, topicBack, topicBackConsumed, updateSession]);

  /**
   * When the user navigates away from a topic page or one of its sub-pages
   * and does not navigate back to a collection page,
   * then clear/reset the collection page scroll position
   */
  useEffect(() => {
    if (scrollPositions?.collectionPage === undefined) return;
    if (!/^\/(topics|collection)\/(.+)$/.test(location.pathname))
      updateSession((state) => ({
        ...state,
        scrollPositions: { ...state.scrollPositions, collectionPage: undefined },
      }));
  }, [location.pathname, scrollPositions?.collectionPage, updateSession]);

  /**
   * When the user navigates away from a topic page or one of its sub-pages
   * and does not navigate back to the discover/topics page,
   * then clear/reset the discover page scroll position
   */
  useEffect(() => {
    if (scrollPositions?.discoverPage === undefined) return;
    if (!/^\/(discover|topics).*$/.test(location.pathname))
      updateSession((state) => ({ ...state, scrollPositions: { ...state.scrollPositions, discoverPage: undefined } }));
  }, [location.pathname, scrollPositions?.discoverPage, updateSession]);
};
